import { useReducer } from 'react';
import { useMutation } from 'urql';
import colors from '../../../../assets/colors/Colors.json';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { loadingReducer, loadingReducerInitialState } from '../../../../reducer/loadingReducer';
import {
  CreateSubjectDocument,
  CreateSubjectMutation,
  CreateSubjectMutationVariables,
  DeleteSubjectsDocument,
  SetSubjectsFlagDocument,
  SetSubjectsFlagMutation,
  SetSubjectsFlagMutationVariables,
  SubjectFlag,
  SubjectInput,
  UpdateSubjectsDocument,
  UpdateSubjectsMutation,
  UpdateSubjectsMutationVariables,
  use_UpdateSubjectsColorsMutation,
  useRoomsListQuery,
  useSubjectGroupsQuery,
  useSubjectsListQuery,
} from '../../../../types/planung-graphql-client-defs';
import { SubjectFormType } from '../../Forms/SubjectForm';

const flagByCheckbox: Record<'subjectHourEnabled' | 'epochEnabled' | 'other' | 'active', SubjectFlag> = {
  subjectHourEnabled: SubjectFlag.SubjectHourEnabled,
  epochEnabled: SubjectFlag.EpochEnabled,
  other: SubjectFlag.Other,
  active: SubjectFlag.Active,
};

/** Das Backend erwartet immer den vollständigen Stand eines Fachs. */
const formToInput = (values: SubjectFormType): SubjectInput => ({
  name: values.name,
  shortName: values.shortName,
  subjectHourEnabled: values.subjectHourEnabled,
  epochEnabled: values.epochEnabled,
  active: values.active,
  other: values.other,
  subjectGroupUuid: values.subjectGroup || null,
  defaultRoomUuids: values.defaultRooms ?? [],
  color: values.color || null,
  colorLabel: values.colorLabel || null,
});

export function useSubject() {
  const context = useMemorizedCacheTag('SUBJECTS');
  const subjectGroupsContext = useMemorizedCacheTag('SUBJECT_GROUPS');
  const roomsContext = useMemorizedCacheTag('ROOMS');

  const [, create] = useMutation<CreateSubjectMutation, CreateSubjectMutationVariables>(CreateSubjectDocument);
  const [, update] = useMutation<UpdateSubjectsMutation, UpdateSubjectsMutationVariables>(UpdateSubjectsDocument);
  const [, remove] = useMutation(DeleteSubjectsDocument);
  const [, setFlag] = useMutation<SetSubjectsFlagMutation, SetSubjectsFlagMutationVariables>(SetSubjectsFlagDocument);
  const [, updateColor] = use_UpdateSubjectsColorsMutation();

  const [loadingState, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);

  const [{ data }] = useSubjectsListQuery({
    context,
  });

  const [{ data: subjectGroupsData }] = useSubjectGroupsQuery({ context: subjectGroupsContext });

  const [{ data: roomsData }] = useRoomsListQuery({
    context: roomsContext,
  });

  const createSubject = async (values: SubjectFormType) => {
    dispatch({ type: 'SET_LOADING', uuids: ['all'] });

    const res = await create({ input: formToInput(values) }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: ['create'] });
    return res;
  };

  const setSubjectGroup = async (subjectUuid: string, subjectGroupUuid: string) => {
    const subject = data?.subjects.find((candidate) => candidate.uuid === subjectUuid);
    if (!subject) {
      return undefined;
    }
    dispatch({ type: 'SET_LOADING', uuids: [subjectUuid] });

    // Es gibt nur das vollständige Ändern: der übrige Stand des Fachs wird unverändert mitgeschickt.
    const res = await update(
      {
        uuid: subjectUuid,
        input: {
          name: subject.name,
          shortName: subject.shortName,
          subjectHourEnabled: subject.subjectHourEnabled,
          epochEnabled: subject.epochEnabled,
          active: subject.active,
          other: subject.other,
          subjectGroupUuid: subjectGroupUuid || null,
          defaultRoomUuids: subject.defaultRooms.map((room) => room.uuid),
          color: subject.timetableConfig?.color,
          colorLabel: subject.timetableConfig?.colorLabel,
        },
      },
      context,
    );
    dispatch({ type: 'REMOVE_LOADING', uuids: [subjectUuid] });
    return res;
  };

  const updateSubject = async (subjectUuid: string, values: SubjectFormType) => {
    dispatch({ type: 'SET_LOADING', uuids: [subjectUuid] });

    const res = await update({ uuid: subjectUuid, input: formToInput(values) }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: [subjectUuid] });
    return res;
  };

  const deleteSubjects = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });

    // Einstellungen und Qualifikationen der Fächer löscht das Backend mit.
    const res = await remove({ uuids }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  /** Je Fach eine zufällige Farbe — wie bei Personen und Klassen bewusst unabhängig gezogen. */
  const setRandomColors = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });

    const res = await Promise.all(
      uuids.map((uuid) =>
        updateColor({ uuid, color: colors[Math.floor(Math.random() * colors.length)].html }, context),
      ),
    );

    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });

    return res;
  };

  const updateCheckboxes = async ({
    type,
    uuids,
    value,
  }: {
    type: 'subjectHourEnabled' | 'epochEnabled' | 'other' | 'active';
    uuids: string[];
    value: boolean;
  }) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });

    const res = await setFlag({ uuids, flag: flagByCheckbox[type], value }, context);

    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });

    return res;
  };

  return {
    createSubject,
    updateSubject,
    deleteSubjects,
    updateCheckboxes,
    setRandomColors,
    setSubjectGroup,
    loadingState,
    data,
    subjectGroupsData,
    roomsData,
  };
}
